import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  color?: 'primary' | 'white' | 'gray';
  'aria-label'?: string;
}

export function LoadingSpinner({ 
  size = 'md', 
  className,
  color = 'primary',
  'aria-label': ariaLabel = 'Loading'
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  const colorClasses = {
    primary: 'border-blueviolet border-t-transparent',
    white: 'border-white border-t-transparent',
    gray: 'border-night-300 border-t-transparent'
  };

  return (
    <div
      className={cn(
        'animate-spin rounded-full border-2',
        sizeClasses[size],
        colorClasses[color],
        className
      )}
      role="status"
      aria-label={ariaLabel}
    >
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
}

interface ButtonLoadingSpinnerProps {
  className?: string;
}

export function ButtonLoadingSpinner({ className }: ButtonLoadingSpinnerProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <LoadingSpinner size="sm" color="white" />
      <span>Loading...</span>
    </div>
  );
}

interface ModalLoadingSpinnerProps {
  message?: string;
  className?: string;
}

export function ModalLoadingSpinner({ 
  message = 'Loading...', 
  className 
}: ModalLoadingSpinnerProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-8 space-y-4', className)} role="status" aria-live="polite">
      <LoadingSpinner size="lg" aria-label={message} />
      <p className="font-body text-sm text-night-500" aria-hidden="true">{message}</p>
    </div>
  );
}

interface InlineLoadingSpinnerProps {
  message?: string;
  className?: string;
}

export function InlineLoadingSpinner({ 
  message = 'Loading...', 
  className 
}: InlineLoadingSpinnerProps) {
  return (
    <div className={cn('flex items-center gap-2', className)} role="status" aria-live="polite">
      <LoadingSpinner size="sm" aria-label={message} />
      <span className="font-body text-sm text-night-500" aria-hidden="true">{message}</span>
    </div>
  );
}